package com.sddamico.draftmachine.ds.forms

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmMotion
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.boxShadowRaw
import com.sddamico.draftmachine.ds.displayInlineFlex
import com.sddamico.draftmachine.ds.roleBody
import com.sddamico.draftmachine.ds.shadowInset
import com.sddamico.draftmachine.ds.transitionRaw
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button as DomButton
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span

/**
 * A brass lever toggle for boolean settings — on = brass track + glowing knob slid right.
 * Controlled (pass [checked]) or uncontrolled (via [defaultChecked]). Port of `components/forms/Switch.jsx`.
 */
@Composable
fun Switch(
  label: String? = null,
  modifier: Modifier = Modifier,
  checked: Boolean? = null,
  defaultChecked: Boolean = false,
  disabled: Boolean = false,
  onChange: ((Boolean) -> Unit)? = null,
) {
  var internal by remember { mutableStateOf(defaultChecked) }
  val on = checked ?: internal
  val toggle = {
    if (!disabled) {
      val next = !on
      if (checked == null) internal = next
      onChange?.invoke(next)
    }
  }

  Div(
    attrs = modifier
      .displayInlineFlex().alignItems(AlignItems.Center).gap(DmSpacing.space3)
      .opacity(if (disabled) 0.5 else 1.0)
      .toAttrs()
  ) {
    DomButton(
      attrs = Modifier
        .position(Position.Relative).width(46.px).height(26.px).flex("0 0 auto").padding(0.px)
        .borderRadius(DmEffects.radiusPill)
        .cursor(if (disabled) Cursor.NotAllowed else Cursor.Pointer)
        .then(
          if (on) Modifier.backgroundColor(DmColor.brass500)
            .border(1.px, LineStyle.Solid, DmColor.brass600)
            .boxShadowRaw("inset 0 1px 0 rgba(232,240,247,.3)")
          else Modifier.backgroundColor(DmColor.surfaceInset)
            .border(1.px, LineStyle.Solid, DmColor.borderStrong)
            .shadowInset()
        )
        .transitionRaw("all ${DmMotion.durBase} ${DmMotion.easeCog}")
        .toAttrs {
          attr("type", "button"); attr("role", "switch"); attr("aria-checked", on.toString())
          if (label != null) attr("aria-label", label)
          if (disabled) attr("disabled", "")
          onClick { toggle() }
        }
    ) {
      Span(
        attrs = Modifier
          .position(Position.Absolute).top(50.percent).left(if (on) 22.px else 2.px)
          .size(20.px).borderRadius(999.px)
          .backgroundColor(if (on) DmColor.steel50 else DmColor.steel300)
          .boxShadowRaw("0 1px 3px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.4)")
          .transitionRaw("left ${DmMotion.durBase} ${DmMotion.easeCog}, background ${DmMotion.durBase}")
          .styleModifier { property("transform", "translateY(-50%)") }
          .toAttrs()
      ) {}
    }
    if (label != null) {
      SpanText(label, Modifier.roleBody().color(DmColor.text).cursor(if (disabled) Cursor.NotAllowed else Cursor.Pointer))
    }
  }
}
